import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeaders() {
  const password = sessionStorage.getItem('admin_password');
  if (!password) return {};
  return {
    Authorization: `Basic ${btoa(':' + password)}`
  };
}

export default function SettingsManager({ onRefresh }) {
  const [contact, setContact] = useState({
    phone: '',
    email: '',
    address: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContact();
  }, []);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/contact`);
      setContact(res.data);
    } catch (error) {
      toast.error('Failed to fetch contact info');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/contact`, contact, { headers: getAuthHeaders() });
      toast.success('Contact info updated');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact info');
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-manager">
      <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Settings</h2>

      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
        {loading ? (
          <div className="space-y-4">
            <div className="skeleton h-10" />
            <div className="skeleton h-10" />
            <div className="skeleton h-20" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
                placeholder="studio@example.com"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                placeholder="+1 234 567 8900"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={contact.address}
                onChange={(e) => setContact({ ...contact, address: e.target.value })}
                placeholder="123 Architecture St, Design City"
                rows={3}
                data-testid="contact-address-input"
              />
            </div>
            <Button type="submit" className="btn-hover" style={{ background: '#b8d71b', color: '#1a1a1a' }} data-testid="save-contact-btn">
              Save Changes
            </Button>
          </form>
        )}
      </Card>

      <Card className="p-6 bg-gradient-to-br from-gray-50 to-white">
        <h3 className="text-lg font-semibold mb-2">Admin Security</h3>
        <p className="text-sm text-gray-600 mb-4">
          To change your admin password, update the <code className="bg-gray-200 px-2 py-1 rounded">ADMIN_PASSWORD</code> in your backend <code className="bg-gray-200 px-2 py-1 rounded">.env</code> file and restart the backend server.
        </p>
        <div className="text-xs text-gray-500">
          <p>Current session is protected and will expire when you logout or close the browser.</p>
        </div>
      </Card>
    </div>
  );
}